import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { apiService } from '../services/apiService';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';

// Dashboard icons for background animation
const dashboardIcons = [
  { emoji: '💰', size: '4xl', opacity: '15' },
  { emoji: '💎', size: '4xl', opacity: '15' },
  { emoji: '💵', size: '4xl', opacity: '15' },
  { emoji: '💳', size: '4xl', opacity: '15' }
];

const BudgetManagement = () => {
  const navigate = useNavigate();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  const categoryData = {
    'Venue': { icon: '🏛️', color: 'from-rose-200 to-pink-300', chartColor: '#F8BBD9' },
    'Catering': { icon: '🍽️', color: 'from-champagne-200 to-gold-300', chartColor: '#F7E7CE' },
    'Photography': { icon: '📸', color: 'from-purple-200 to-lavender-300', chartColor: '#E6D7FF' },
    'Flowers': { icon: '🌸', color: 'from-pink-200 to-rose-300', chartColor: '#FFB6C1' },
    'Music': { icon: '🎵', color: 'from-blue-200 to-indigo-300', chartColor: '#B6D7FF' },
    'Attire': { icon: '👗', color: 'from-mauve-200 to-purple-300', chartColor: '#D8BFD8' },
    'Transportation': { icon: '🚗', color: 'from-teal-200 to-cyan-300', chartColor: '#B0E0E6' },
    'Invitations': { icon: '💌', color: 'from-blush-200 to-pink-300', chartColor: '#FFCCCB' },
    'Rings': { icon: '💍', color: 'from-gold-200 to-yellow-300', chartColor: '#FFD700' },
    'Miscellaneous': { icon: '✨', color: 'from-emerald-200 to-mint-300', chartColor: '#98FB98' }
  };

  useEffect(() => {
    loadBudget();
    setIsVisible(true);
  }, []);

  const loadBudget = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!token || !userId) {
      navigate('/');
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.budget.getSavedBudget(userId);
      setBudget(response.data.budget);
    } catch (error) {
      console.error('Error loading budget:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    console.log('🗑️ DELETE CONFIRMATION - Using NotificationModal');
    try {
      await apiService.budget.deleteBudget(budget.id);
      setBudget(null);
      
      // Show success notification
      setNotification({
        show: true,
        title: 'Budget Deleted',
        message: '🗑️ Your budget has been deleted successfully.',
        type: 'success'
      });
      
      // Navigate to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error deleting budget:', error);
      
      // Show error notification
      setNotification({
        show: true,
        title: 'Delete Failed',
        message: 'Failed to delete budget. Please try again.',
        type: 'error'
      });
    }
  };

  const getChartData = () => {
    if (!budget?.allocations) return null;

    return {
      labels: budget.allocations.map(a => a.category),
      datasets: [{
        data: budget.allocations.map(a => a.amount),
        backgroundColor: budget.allocations.map(a => categoryData[a.category]?.chartColor || '#CCCCCC'),
        borderWidth: 3,
        borderColor: '#ffffff',
        hoverBorderWidth: 5,
        hoverBorderColor: '#ffffff'
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            const percentage = ((value / budget.total_budget) * 100).toFixed(1);
            return `$${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <BackgroundIcons icons={dashboardIcons} />
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce">
            <span className="text-4xl animate-pulse">💰</span>
          </div>
          <p className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
            Loading your budget...
          </p>
        </div>

        {/* Notification Modal */}
        <NotificationModal
          isOpen={notification.show}
          onClose={() => setNotification({ ...notification, show: false })}
          title={notification.title}
          message={notification.message}
          type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        />
      </div>
    );
  }

  // Empty State - No Budget
  if (!budget) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
        <BackgroundIcons icons={dashboardIcons} />
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
              <span className="text-4xl animate-pulse">💰</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide">
              No Budget Yet
            </h1>
            
            <div className="flex items-center justify-center mb-6">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="mx-4 text-3xl animate-bounce">💎</div>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            </div>
            
            <p className="text-2xl text-gray-700 font-light italic mb-8">
              Start planning your dream wedding by creating your first budget allocation ✨
            </p>
            
            <button
              onClick={() => navigate('/wedding-blueprint/budget-allocator')}
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-12 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl">✨</span>
              Create Your First Budget
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </button>
          </div>
        </div>

        {/* Notification Modal */}
        <NotificationModal
          isOpen={notification.show}
          onClose={() => setNotification({ ...notification, show: false })}
          title={notification.title}
          message={notification.message}
          type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        />
      </div>
    );
  }

  // Budget Exists - Show Details
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={dashboardIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 relative">
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </button>
          </div>
          
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">💰</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide animate-fadeInUp">
            Your Wedding Budget
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">💎</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className="text-2xl text-gray-700 font-light italic animate-fadeInUp delay-300">
            Last updated: {new Date(budget.updated_at).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              Total: ${budget.total_budget.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Chart and Categories Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Chart */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 h-full">
              <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6 text-center">
                Budget Distribution
              </h2>
              <div className="h-80">
                <Doughnut data={getChartData()} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 h-full">
              <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6">
                Category Breakdown
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {budget.allocations.map((allocation, idx) => (
                  <div 
                    key={idx}
                    className={`bg-gradient-to-r ${categoryData[allocation.category]?.color || 'from-gray-100 to-gray-200'} rounded-2xl p-4 border border-white/50 hover:shadow-xl transition-all duration-300 hover:scale-105`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">{categoryData[allocation.category]?.icon || '✨'}</span>
                        <div>
                          <h3 className="font-bold text-gray-800">{allocation.category}</h3>
                          <p className="text-sm text-gray-600">{allocation.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-800">
                          ${allocation.amount.toLocaleString()}
                        </p>
                        <p className="text-sm font-bold text-gray-600">
                          {allocation.percentage.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center mt-12">
          <button
            onClick={() => navigate('/wedding-blueprint/budget-allocator')}
            className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
          >
            <span className="mr-2">✏️</span>
            Edit Budget
            <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-8 py-4 border-2 border-red-300 text-red-600 rounded-2xl font-bold text-lg hover:bg-red-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            🗑️ Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <NotificationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        type="warning"
        title="Delete Budget?"
        message="Are you sure you want to delete your budget? This action cannot be undone."
        isConfirmation={true}
        onConfirm={handleDelete}
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      />
    </div>
  );
};

export default BudgetManagement;
